"use client";

import { useEffect, useState } from "react";
import { useCustomSession } from "./useCustomSession";

interface Role {
	id: string;
	name: string;
	description: string;
	weight: number;
	color: string;
	icon: string;
	createdAt: Date;
}

interface UserPermissions {
	permissions: string[];
	role: string | null;
	roleData: Role | null;
	isLoading: boolean;
}

export function useUserPermissions(): UserPermissions {
	const { session, loading } = useCustomSession();
	const [userPermissions, setUserPermissions] = useState<UserPermissions>({
		permissions: [],
		role: null,
		roleData: null,
		isLoading: true,
	});

	// Fetch permissions when user is logged in
	useEffect(() => {
		if (loading || !session?.user?.id) {
			return;
		}

		let cancelled = false;

		const fetchUserPermissions = async () => {
			try {
				const response = await fetch("/api/user/permissions");
				if (!cancelled) {
					if (response.ok) {
						const data = await response.json();
						setUserPermissions({
							permissions: data.permissions || [],
							role: data.roleName || null,
							roleData: data.roleData || null,
							isLoading: false,
						});
					} else {
						setUserPermissions({
							permissions: [],
							role: null,
							roleData: null,
							isLoading: false,
						});
					}
				}
			} catch (error) {
				if (!cancelled) {
					console.error("Error fetching user permissions:", error);
					setUserPermissions({
						permissions: [],
						role: null,
						roleData: null,
						isLoading: false,
					});
				}
			}
		};

		fetchUserPermissions();

		return () => {
			cancelled = true;
		};
	}, [session?.user?.id, loading]);

	// Return derived state for logged out users
	if (!loading && !session?.user?.id) {
		return {
			permissions: [],
			role: null,
			roleData: null,
			isLoading: false,
		};
	}

	return userPermissions;
}
